import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CheckCircle,
  Heart,
  Sparkles,
  Send,
  ArrowRight,
  Loader2,
  HandHeart,
} from 'lucide-react'
import { connectCardSchema } from '../../utils/validators'
import { addDocument } from '../../services/firebase'
import { sendConnectCardEmail } from '../../services/email'
import { Input, Select, Textarea, Checkbox } from '../../components/common'

const interestOptions = [
  { value: 'salvation', label: 'Learning about salvation' },
  { value: 'baptism', label: 'Getting baptized' },
  { value: 'small_group', label: 'Joining a small group' },
  { value: 'volunteer', label: 'Serving / volunteering' },
  { value: 'membership', label: 'Becoming a member' },
  { value: 'prayer', label: 'Receiving prayer' },
]

const serviceOptions = [
  { value: 'this_sunday', label: 'This past Sunday' },
  { value: 'last_sunday', label: 'Last Sunday' },
  { value: 'midweek', label: 'Midweek service' },
  { value: 'other', label: 'Another service / event' },
]

const ConnectCard = () => {
  const [submitted, setSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(connectCardSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      serviceAttended: '',
      visitorType: 'first_time',
      interests: [],
      message: '',
    },
  })

  const onSubmit = async (data) => {
    try {
      await addDocument('formSubmissions', {
        ...data,
        formType: 'connect-card',
        status: 'new',
      })
      // Fire-and-forget email notification — don't block UI on email failure
      sendConnectCardEmail(data).catch((err) =>
        console.warn('Connect card email failed:', err)
      )
      setSubmitted(true)
      reset()
    } catch (error) {
      console.error('Error submitting connect card:', error)
    }
  }

  return (
    <>
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-1/4 w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <HandHeart className="w-4 h-4 text-accent-400" />
              We're glad you came!
            </motion.span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
              Connect <span className="bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent">Card</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Thanks for visiting Deeper Life Bible Church Clarksville. Fill out
              this quick card so we can welcome you properly and connect you
              with our church family.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-gradient-to-b from-light-100 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <motion.div
              className="bg-white rounded-3xl shadow-soft p-10 md:p-14 border border-gray-100 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <motion.div
                className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
              >
                <CheckCircle className="w-12 h-12 text-white" />
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-4">
                Thank you for connecting!
              </h2>
              <p className="text-lg text-dark-300 mb-8 max-w-xl mx-auto">
                We've received your card and someone from our welcome team will
                reach out soon. We're so excited to get to know you.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/sermons">
                  <motion.button
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold shadow-lg shadow-primary-500/25"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Watch a Sermon
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
                <Link to="/new-here">
                  <motion.button
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl border-2 border-secondary-500 text-secondary-600 font-bold hover:bg-secondary-50 transition-colors"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    I'm New Here
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.div
              className="bg-white rounded-3xl shadow-soft p-8 md:p-12 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="text-center mb-8">
                <motion.span
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold mb-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <Sparkles className="w-4 h-4" />
                  Just a minute to fill out
                </motion.span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100">
                  Let's <span className="gradient-text">stay in touch</span>
                </h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="First Name"
                    required
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <Input
                    label="Last Name"
                    required
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Email Address"
                    type="email"
                    required
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <Input
                    label="Phone (optional)"
                    type="tel"
                    error={errors.phone?.message}
                    {...register('phone')}
                  />
                </div>

                <Select
                  label="Which service did you attend?"
                  required
                  options={serviceOptions}
                  error={errors.serviceAttended?.message}
                  {...register('serviceAttended')}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Is this your first time visiting? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-primary-400 transition-colors has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                      <input
                        type="radio"
                        value="first_time"
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        {...register('visitorType')}
                      />
                      <span className="font-medium text-dark-100">Yes, first time</span>
                    </label>
                    <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-primary-400 transition-colors has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                      <input
                        type="radio"
                        value="returning"
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                        {...register('visitorType')}
                      />
                      <span className="font-medium text-dark-100">Returning visitor</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    I'd love more info about... <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {interestOptions.map((opt) => (
                      <Checkbox
                        key={opt.value}
                        label={opt.label}
                        value={opt.value}
                        {...register('interests')}
                      />
                    ))}
                  </div>
                </div>

                <Textarea
                  label="Anything else you'd like to share? (optional)"
                  rows={4}
                  placeholder="Prayer requests, questions, or just say hi…"
                  error={errors.message?.message}
                  {...register('message')}
                />

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold shadow-lg shadow-accent-500/25 disabled:opacity-50"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send My Connect Card
                    </>
                  )}
                </motion.button>

                <p className="text-center text-sm text-dark-300">
                  Your info stays with our welcome team — we never share it.
                </p>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 text-white mx-auto mb-4" fill="currentColor" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            We can't wait to meet you again.
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Whether it's your first time or your fiftieth, you belong here.
          </p>
        </div>
      </section>
    </>
  )
}

export default ConnectCard
